<template>
  <div class="user-form">
    <avatar-form
      v-if="isUpdate"
      :user="user"
      @change="avatarChange"
      @avatarRemove="userAvatarRemoved"
      :avatarLoading="avatarLoading"
    />

    <v-form
      ref="form"
      v-if="form"
      style="max-width: 600px"
      @submit.prevent="onSubmit"
    >
      <v-row v-if="isUpdate">
        <v-col cols="12" sm="6">
          <label class="text-field-label">Status</label>
          <v-text-field
            flat
            solo
            readonly
            hide-details="auto"
            class="mt-2"
            v-model="user.status"
          >
            <v-tooltip slot="append" top v-if="!user.active">
              <template v-slot:activator="{ on }">
                <v-icon color="warning" v-on="on">{{ icons.alert }}</v-icon>
              </template>
              <span>Reason: {{ user.reason_for_blocking }}</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <label class="text-field-label">Name</label>
          <v-text-field
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            placeholder="Name"
            v-model="form.full_name"
            :error-messages="form.$getError('full_name')"
            :loading="form.$busy"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="text-field-label">Email</label>
          <v-text-field
            flat
            solo
            required
            autofocus
            hide-details="auto"
            class="mt-2"
            type="email"
            placeholder="Email"
            v-model="form.email"
            :error-messages="form.$getError('email')"
            :loading="form.$busy"
          >
            <v-tooltip slot="append" top v-if="isUpdate">
              <template v-slot:activator="{ on }">
                <v-icon color="success" v-on="on" v-if="isEmailVerified">{{
                  icons.check
                }}</v-icon>
                <v-icon color="warning" v-on="on" v-else>{{
                  icons.alert
                }}</v-icon>
              </template>
              <span v-if="isEmailVerified">Email Verified</span>
              <span v-else>Unverified email</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6" v-if="!isUpdate" class="mb-5">
          <label class="text-field-label">Password</label>
          <v-text-field
            v-model="form.password"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :error-messages="form.$getError('password')"
            :loading="form.$busy"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <label class="text-field-label">Phone number</label>
          <v-text-field
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            v-model="form.phone_number"
            placeholder="Phone number"
            :error-messages="form.$getError('phone_number')"
            :loading="form.$busy"
          >
            <v-tooltip slot="append" top v-if="isUpdate">
              <template v-slot:activator="{ on }">
                <v-icon color="success" v-on="on" v-if="isPhoneVerified">{{
                  icons.check
                }}</v-icon>
                <v-icon color="warning" v-on="on" v-else>{{
                  icons.alert
                }}</v-icon>
              </template>
              <span v-if="isPhoneVerified">Phone Number Verified</span>
              <span v-else>Unverified Phone Number</span>
            </v-tooltip>
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="text-field-label">Birthday</label>
          <v-text-field
            v-model="form.birth_date"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="12">
          <label class="text-field-label">Address</label>
          <v-text-field
            v-model="form.address_1"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6">
          <label class="text-field-label">City or Suburb</label>
          <v-text-field
            v-model="form.city"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <label class="text-field-label">Postcode</label>
          <v-text-field
            v-model="form.postal_code"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6" sm="6">
          <label class="text-field-label">Country</label>
          <v-text-field
            v-model="form.country"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
        <v-col cols="6" sm="6">
          <label class="text-field-label">State</label>
          <v-text-field
            v-model="form.state"
            flat
            solo
            required
            hide-details="auto"
            class="mt-2"
            :readonly="isUpdate"
          >
          </v-text-field>
        </v-col>
      </v-row>

      <div class="mt-4">
        <v-btn
          type="submit"
          color="primary"
          depressed
          class="mr-4 px-6"
          height="40px"
          :loading="form.$busy"
          >{{ actionLabel }}</v-btn
        >
        <v-btn
          v-if="isUpdate"
          color="red"
          dark
          depressed
          class="mr-4 px-6"
          height="40px"
          @click="deleteDialog = true"
          :loading="form.$busy"
          >Delete</v-btn
        >
      </div>
    </v-form>

    <v-dialog v-if="user" v-model="deleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Delete ?</v-card-title>

        <v-card-text
          >Are you sure you want to delete this account ?</v-card-text
        >

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="deleteDialog = false" :loading="form.$busy"
            >Cancel</v-btn
          >

          <v-btn color="red" text @click="onDelete" :loading="form.$busy"
            >Confirm</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>
  </div>
</template>
<script>
import User from '@/models/User'
import { mdiCheck, mdiAlertCircleOutline } from '@mdi/js'
import { mapActions, mapMutations } from 'vuex'
import AvatarForm from './AvatarForm'
import Form from '@/utils/form'

export default {
  components: {
    AvatarForm
  },

  props: {
    user: {
      type: [User, Object]
    }
  },

  data() {
    return {
      icons: {
        check: mdiCheck,
        alert: mdiAlertCircleOutline
      },
      avatarLoading: false,
      deleteDialog: false,
      form: new Form({
        email: null,
        full_name: null,
        password: null,
        password_confirmation: null,
        address_1: null,
        city: null,
        postal_code: null,
        state: null,
        country: null
      }),
      snackbar: {
        show: false,
        message: null,
        color: ''
      }
    }
  },

  computed: {
    submit() {
      return this.isUpdate ? this.updateUser : this.createUser
    },

    request() {
      let post = this.form.$data()

      if (this.isUpdate) {
        delete post.password
        delete post.password_confirmation
      } else {
        post.password_confirmation = post.password
      }

      return post
    },

    actionLabel() {
      return this.isUpdate ? 'Update' : 'Save'
    },

    isUpdate() {
      return !!this.user
    },

    isPhoneVerified() {
      return this.user && this.user.phone_number_verified
    },

    isEmailVerified() {
      return this.user && this.user.email_verified
    }
  },

  watch: {
    user(value) {
      this.initForm()
    }
  },

  methods: {
    ...mapActions({
      changeAvatar: 'user/changeAvatar',
      updateUser: 'user/updateUser',
      createUser: 'user/createUser',
      deleteUser: 'user/deleteUser'
    }),

    ...mapMutations({
      setSelectedUserAvatar: 'user/setSelectedUserAvatar',
      userAvatarRemoved: 'user/userAvatarRemoved'
    }),

    initForm() {
      if (this.user) {
        this.form = new Form(this.user)
      }
    },

    async onSubmit() {
      if (this.form.$busy) return
      this.form.$setLoading(true)

      await this.submit(this.request)
        .then(data => {
          if (this.isUpdate) {
            this.form.$setLoading(false)
            this.showSnackbar('User successfully updated!', 'success')
          } else {
            this.$router.push({
              name: 'user.details',
              params: {
                id: data.data.data.id
              }
            })
          }
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.showSnackbar(this.form.$errorMessage, 'danger')
        })
    },

    async onDelete() {
      if (this.form.$busy) return
      this.form.$setLoading(true)

      await this.deleteUser({
        id: this.user.id
      })
        .then(() => {
          this.form.$setLoading(false)
          this.deleteDialog = false
          this.showSnackbar('User deleted successfully!')
          this.$router.replace('/users')
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.showSnackbar(this.form.$errorMessage, 'danger')
        })
    },

    async avatarChange(image) {
      this.avatarLoading = true
      let data = new FormData()
      data.append('user_id', this.user.id)
      data.append('avatar', image)
      await this.changeAvatar(data)
      this.avatarLoading = false
      this.showSnackbar('Avatar successfully updated!', 'success')
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }
  },

  mounted() {
    this.initForm()
  }
}
</script>
